'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { WrapSegment } from '@/components/common/WrapSegment';
import { getParsedValue } from '@/helpers/formatters';
import type { VaultV1ActionPanelData } from '../vaultV1ActionPanelData';
import { useVaultV1DepositLogic } from '../../hooks/useVaultV1DepositLogic';
import VaultV1OperationShell, { VaultV1FieldError } from './VaultV1OperationShell';
import { VaultV1DepositReviewDialog } from './VaultV1ReviewDialog';
import { VaultV1Summary } from './VaultV1Summary';
interface VaultV1OperationDepositProps {
  vaultData: VaultV1ActionPanelData | null;
  vaultAddress: Address;
}
const VaultV1OperationDeposit = t0 => {
  const $ = _c(64);
  const {
    vaultAddress,
    vaultData
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const t1 = vaultData?.asset;
  let t2;
  if ($[0] !== t1) {
    t2 = getParsedValue(t1, parseApiAsset);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const vaultAsset = t2;
  let t3;
  if ($[2] !== vaultAddress || $[3] !== vaultData) {
    t3 = {
      vaultData,
      vaultAddress
    };
    $[2] = vaultAddress;
    $[3] = vaultData;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    maxDeposit,
    userVaultAssets,
    finalPosition,
    tooltipMessage,
    isLoading,
    isMaxLoading,
    isWrappingToggleLoading,
    error,
    disabledLabel,
    canTransfer,
    bundleCanTransfer,
    refetchPosition,
    resetInput,
    isWNativeVault,
    nativeSymbol,
    isWrapping,
    onWrappingChange,
    erc20Amount,
    nativeAmount,
    wrappableNativeBalance
  } = useVaultV1DepositLogic(t3);
  let t4;
  if ($[5] !== refetchPosition || $[6] !== resetInput) {
    t4 = () => {
      resetInput();
      refetchPosition();
    };
    $[5] = refetchPosition;
    $[6] = resetInput;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onTxSuccess = t4;
  let t5;
  if ($[8] !== erc20Amount || $[9] !== finalPosition || $[10] !== inputAmount || $[11] !== nativeAmount || $[12] !== onTxSuccess || $[13] !== toggleDialog || $[14] !== userVaultAssets || $[15] !== vaultAddress || $[16] !== vaultAsset) {
    t5 = () => {
      if (!isDefined(userVaultAssets) || !vaultAsset) {
        throw new Error("Missing data to open review dialog");
      }
      toggleDialog(<SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="494px" />}><VaultV1DepositReviewDialog vaultAddress={vaultAddress} inputAmount={inputAmount} erc20Amount={erc20Amount} nativeAmount={nativeAmount} vaultAsset={vaultAsset} userPosition={userVaultAssets} finalPosition={finalPosition} onTxSuccess={onTxSuccess} entryPoint="vault_page" /></SectionErrorBoundary>);
    };
    $[8] = erc20Amount;
    $[9] = finalPosition;
    $[10] = inputAmount;
    $[11] = nativeAmount;
    $[12] = onTxSuccess;
    $[13] = toggleDialog;
    $[14] = userVaultAssets;
    $[15] = vaultAddress;
    $[16] = vaultAsset;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const openReviewDialog = t5;
  const t6 = `Deposit ${vaultAsset?.symbol}`;
  const t7 = maxDeposit?.value;
  let t8;
  if ($[18] !== inputAmount || $[19] !== isMax || $[20] !== isMaxLoading || $[21] !== onMaxClick || $[22] !== setInputAmount || $[23] !== t6 || $[24] !== t7 || $[25] !== vaultAsset) {
    t8 = <LargeAssetInput header={t6} token={vaultAsset} amount={inputAmount} onChange={setInputAmount} maxValue={t7} isMaxLoading={isMaxLoading} isMax={isMax} onMaxClick={onMaxClick} autoFocus={true} />;
    $[18] = inputAmount;
    $[19] = isMax;
    $[20] = isMaxLoading;
    $[21] = onMaxClick;
    $[22] = setInputAmount;
    $[23] = t6;
    $[24] = t7;
    $[25] = vaultAsset;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== error.userBalanceError) {
    t9 = <VaultV1FieldError error={error.userBalanceError} message="Error fetching wallet balance." />;
    $[27] = error.userBalanceError;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== error.nativeBalanceError) {
    t10 = <VaultV1FieldError error={error.nativeBalanceError} message="Error fetching native balance." />;
    $[29] = error.nativeBalanceError;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== error.userVaultPositionError) {
    t11 = <VaultV1FieldError error={error.userVaultPositionError} message="Error fetching vault position." />;
    $[31] = error.userVaultPositionError;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t9;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const t13 = vaultAsset?.symbol;
  let t14;
  if ($[37] !== isWNativeVault || $[38] !== isWrapping || $[39] !== isWrappingToggleLoading || $[40] !== nativeSymbol || $[41] !== onWrappingChange || $[42] !== t13 || $[43] !== wrappableNativeBalance) {
    t14 = <WrapSegment id="vault-deposit" enabled={true} isWNative={isWNativeVault} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={t13} nativeSymbol={nativeSymbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[37] = isWNativeVault;
    $[38] = isWrapping;
    $[39] = isWrappingToggleLoading;
    $[40] = nativeSymbol;
    $[41] = onWrappingChange;
    $[42] = t13;
    $[43] = wrappableNativeBalance;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== finalPosition || $[46] !== isLoading || $[47] !== userVaultAssets || $[48] !== vaultAddress || $[49] !== vaultAsset || $[50] !== vaultData) {
    t15 = <VaultV1Summary vaultAsset={vaultAsset} vaultAddress={vaultAddress} vaultData={vaultData} initialPosition={userVaultAssets} finalPosition={finalPosition} isLoading={isLoading} />;
    $[45] = finalPosition;
    $[46] = isLoading;
    $[47] = userVaultAssets;
    $[48] = vaultAddress;
    $[49] = vaultAsset;
    $[50] = vaultData;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  const t16 = vaultAsset?.address;
  const t17 = !bundleCanTransfer;
  let t18;
  if ($[52] !== canTransfer || $[53] !== disabledLabel || $[54] !== isLoading || $[55] !== openReviewDialog || $[56] !== t12 || $[57] !== t14 || $[58] !== t15 || $[59] !== t16 || $[60] !== t17 || $[61] !== t8 || $[62] !== tooltipMessage) {
    t18 = <VaultV1OperationShell assetInput={t8} fieldErrors={t12} wrappingControl={t14} summary={t15} assetAddress={t16} canTransfer={canTransfer} isBundleNotAuthorized={t17} handleFinalize={openReviewDialog} isLoading={isLoading} reviewLabel="Review Deposit" disabledLabel={disabledLabel} tooltipMessage={tooltipMessage} />;
    $[52] = canTransfer;
    $[53] = disabledLabel;
    $[54] = isLoading;
    $[55] = openReviewDialog;
    $[56] = t12;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t8;
    $[62] = tooltipMessage;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  return t18;
};
export default VaultV1OperationDeposit;